import { useEffect, useState } from 'react';
import { useAuth } from './context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import OfficialAvatar from './components/OfficialAvatar';

interface Adoption {
  id: string;
  adoptedAt: number;
  notes: string;
  notificationPreferences: any;
  official: {
    id: string;
    name: string;
    photoUrl: string;
    personType: string;
    officeName: string;
    officeLevel: string;
    state: string;
    party: string;
    accountabilityScore: number;
    adoptionCount: number;
  };
}

export default function MyAdoptionsPage() {
  const { token, isAuthenticated } = useAuth();
  const [adoptions, setAdoptions] = useState<Adoption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    fetchAdoptions();
  }, [isAuthenticated]);

  const fetchAdoptions = async () => {
    try {
      const response = await fetch('/api/adoptions/me', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch adoptions');
      }

      const data = await response.json();
      setAdoptions(data.adoptions);
    } catch (err: any) {
      console.error('Failed to fetch adoptions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <svg
            className="animate-spin h-12 w-12 text-blue-500 mx-auto mb-4"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-gray-400">Loading your adoptions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-xl p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Error Loading Adoptions</h1>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={fetchAdoptions}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (adoptions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <svg
              className="w-24 h-24 text-gray-600 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <h1 className="text-3xl font-bold text-white mb-2">No Adoptions Yet</h1>
            <p className="text-gray-400 mb-6">
              You haven't adopted any officials yet. Start monitoring the cowards that matter to
              you.
            </p>
            <Link
              to="/officials"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition"
            >
              Browse Officials
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            My Adoptions ({adoptions.length})
          </h1>
          <p className="text-gray-400">
            Keep track of the officials you're monitoring
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {adoptions.map((adoption) => (
            <Link
              key={adoption.id}
              to={`/officials/${adoption.official.id}`}
              className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-750 transition group"
            >
              <div className="relative">
                <OfficialAvatar
                  name={adoption.official.name}
                  photoUrl={adoption.official.photoUrl}
                  alt={adoption.official.name}
                  className="w-full h-56 bg-gray-700 flex items-center justify-center"
                  imgClassName="w-full h-56 object-cover group-hover:opacity-90 transition"
                  initialsClassName="text-4xl font-black text-gray-300 uppercase"
                />
                <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-semibold">
                  Adopted
                </div>
              </div>

              <div className="p-4">
                <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-blue-400 transition">
                  {adoption.official.name}
                </h3>
                <p className="text-gray-400 text-sm mb-3">{adoption.official.officeName}</p>

                <div className="flex flex-wrap gap-2 mb-3">
                  {adoption.official.state && (
                    <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">
                      {adoption.official.state}
                    </span>
                  )}
                  {adoption.official.party && (
                    <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">
                      {adoption.official.party}
                    </span>
                  )}
                  <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded capitalize">
                    {adoption.official.personType}
                  </span>
                </div>

                {adoption.official.accountabilityScore !== null && (
                  <div className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-400">Accountability Score</span>
                      <span className="text-white font-semibold">
                        {adoption.official.accountabilityScore}/100
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          adoption.official.accountabilityScore >= 70
                            ? 'bg-red-500'
                            : adoption.official.accountabilityScore >= 40
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${adoption.official.accountabilityScore}%` }}
                      />
                    </div>
                  </div>
                )}

                {adoption.notes && (
                  <div className="bg-gray-700/50 rounded p-2 mb-3">
                    <p className="text-sm text-gray-300 italic">"{adoption.notes}"</p>
                  </div>
                )}

                <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-700">
                  <span>
                    Adopted {new Date(adoption.adoptedAt).toLocaleDateString()}
                  </span>
                  <span>{adoption.official.adoptionCount} adopters</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
